import {isFeatureEnabled} from 'feature-flags';
import {
  useLocalStorage,
  Dropdown,
  Button,
  Label,
  Separator,
  DropdownItem,
  ButtonVariant,
} from 'platform/components';
import {Show, Space} from 'platform/foundation';
import {match} from 'ts-pattern';

import {Fragment, ReactNode, useCallback, useMemo} from 'react';

import {groupBy, indexBy, isNil, toPairs, isNotNil} from 'ramda';
import {isNotNilOrEmpty} from 'ramda-adjunct';

import {
  DefinitionWithTemplatesResponseBody,
  TemplateResponseBody,
  useGetDefinitionTemplateListQuery,
} from '@dms/api/core';
import {useGetAuthorizationProfilesQuery} from '@dms/api/security';
import featureFlags from '@dms/feature-flags';
import i18n from '@dms/i18n';

import {suffixTestId, TestIdProps} from 'shared/web';

import {getLocaleLabel} from '../utils/getLocaleLabel';
import {useGetCurrentBranch} from './useGetCurrentBranch';
import {useTenant} from './useTenant';

const OTHERS_LOCALE_KEY = '__others__';

type LocaleTemplateGroup<T> = {
  key: string;
  label: string;
  templates: T[];
};

const groupTemplatesByLocale = <T extends {locale?: string | null}>(
  templates: T[],
  customerPreferredLocale?: string | null
): LocaleTemplateGroup<T>[] => {
  const grouped = groupBy<T>((template) => template.locale ?? OTHERS_LOCALE_KEY, templates);
  return toPairs(grouped)
    .map(([key, items]) => ({
      key,
      label:
        key === OTHERS_LOCALE_KEY ? i18n.t('general.labels.others') : (getLocaleLabel(key) ?? key),
      templates: items ?? [],
    }))
    .sort((a, b) => {
      const isAPreferred = isNotNil(customerPreferredLocale) && a.key === customerPreferredLocale;
      const isBPreferred = isNotNil(customerPreferredLocale) && b.key === customerPreferredLocale;
      const isAOthers = a.key === OTHERS_LOCALE_KEY;
      const isBOthers = b.key === OTHERS_LOCALE_KEY;

      return match({isAPreferred, isBPreferred, isAOthers, isBOthers})
        .with({isAPreferred: true}, () => -1)
        .with({isBPreferred: true}, () => 1)
        .with({isAOthers: true}, () => 1)
        .with({isBOthers: true}, () => -1)
        .otherwise(() => a.label.localeCompare(b.label));
    });
};

export const SELECTED_AUTH_PROFILE_KEY = 'selected_auth_profile';

type SelectedAuthProfile = {
  authorizationProfileId: string;
  authorizationId: string;
};

export type TemplateWithPrimary = TemplateResponseBody & {
  isPrimary: boolean;
};

type TemplateReturnType = {
  options: {label: string | undefined; options: {label: string; value: string}[]}[];
  defaultTemplates: TemplateWithPrimary[];
  selectedTemplates: TemplateWithPrimary[];
  primaryTemplate: TemplateWithPrimary | undefined;
  selectedAuthProfileLabel: string | null;
  definition: DefinitionWithTemplatesResponseBody | undefined;
  getDropdownComponent: (
    props: {
      onChange: (templateId: string) => void | Promise<void>;
      title: string | undefined;
      isDisabled?: boolean;
      variant?: Extract<ButtonVariant, 'secondary' | 'outlined'>;
      customerPreferredLocale?: string | null;
    } & TestIdProps
  ) => ReactNode;
};

export type DocumentCode =
  | 'agreement-on-future'
  | 'brokerage-internal-invoice-document'
  | 'brokerage-internal-invoice-document-with-vat'
  | 'cancel-issue-vehicle-warehouse-movement'
  | 'cancel-receive-vehicle-warehouse-movement'
  | 'complaint'
  | 'purchase-contract'
  | 'handover-protocol'
  | 'sale-handover-protocol'
  | 'internal-corrective-tax-document'
  | 'internal-invoice-document'
  | 'internal-invoice-document-with-vat'
  | 'invoice'
  | 'issue-vehicle-warehouse-movement'
  | 'offsetting-of-liabilities-and-receivables'
  | 'power-of-attorney'
  | 'purchase-brokerage-cancelation'
  | 'purchase-brokerage-contract'
  | 'purchase-brokerage-handover-protocol'
  | 'purchase-offer'
  | 'receive-vehicle-warehouse-movement'
  | 'sale-brokerage-contract'
  | 'sale-contract'
  | 'sale-offer'
  | 'service-case-order-offer'
  | 'service-case-order-sheet'
  | 'service-order-claim'
  | 'service-order-issue'
  | 'service-order-parts-return'
  | 'test-drive'
  | 'vehicle-condition'
  | 'warehouse-corrective-receipt'
  | 'warehouse-corrective-receipt-html'
  | 'warehouse-issue'
  | 'warehouse-issue-html'
  | 'warehouse-issue-note-correction'
  | 'warehouse-corrective-issue-html'
  | 'warehouse-offer'
  | 'direct-sale-offer-html'
  | 'warehouse-receipt'
  | 'warehouse-receipt-html'
  | 'warehouse-supplier-order'
  | 'warehouse-supplier-order-html'
  | 'windshield-paper'
  | 'workshop-rental_order_contract'
  | 'workshop-rental_order_return'
  | 'workshop-fast_track';

export const useDocumentTemplates = () => {
  const [selectedAuthProfile] = useLocalStorage<SelectedAuthProfile | null>(
    SELECTED_AUTH_PROFILE_KEY,
    null
  );
  const {activeBranchId} = useGetCurrentBranch();
  const {tenant, isMultiLocale} = useTenant();

  const isAuthProfilesTemplatesEnabled = isFeatureEnabled(featureFlags.AUTH_PROFILES_TEMPLATES);
  const selectedAuthProfileId = selectedAuthProfile?.authorizationProfileId;

  const {
    data: templates,
    isLoading: isTemplatesLoading,
    isError: isTemplatesError,
  } = useGetDefinitionTemplateListQuery({});

  const {
    data: authorizationProfiles,
    isLoading: isLoadingAuthorizationProfiles,
    isError: isErrorAuthorizationProfiles,
  } = useGetAuthorizationProfilesQuery(
    {branchId: activeBranchId, 'x-tenant': tenant?.id ?? ''},
    {skip: isNil(tenant) || !isAuthProfilesTemplatesEnabled}
  );
  const isLoading = isTemplatesLoading || isLoadingAuthorizationProfiles;
  const isError = isTemplatesError || isErrorAuthorizationProfiles;

  const authorizationProfilesById = useMemo(
    () => indexBy((item) => item.id, authorizationProfiles ?? []),
    [authorizationProfiles]
  );

  const getTemplatesByCode = useCallback(
    (documentCode: DocumentCode): TemplateReturnType => {
      const definition = templates?.definitions.find(
        (definition) => definition.documentCode === documentCode
      );

      if (!definition || !definition.templates) {
        return {
          defaultTemplates: [],
          selectedTemplates: [],
          primaryTemplate: undefined,
          selectedAuthProfileLabel: null,
          options: [],
          definition: undefined,
          getDropdownComponent: () => null,
        };
      }

      const defaultTemplates = definition.templates
        .filter((template) => isNil(template.authorizationProfileId) && !template.isHidden)
        .map(addIsPrimaryAttribute(definition))
        .sort(sortByIsPrimary);

      const selectedTemplates =
        isNotNilOrEmpty(selectedAuthProfileId) && isAuthProfilesTemplatesEnabled
          ? definition.templates
              .filter(
                (template) =>
                  template.authorizationProfileId === selectedAuthProfileId && !template.isHidden
              )
              .map(addIsPrimaryAttribute(definition))
              .sort(sortByIsPrimary)
          : [];

      const selectedAuthProfileLabel =
        selectedAuthProfileId && isAuthProfilesTemplatesEnabled
          ? (authorizationProfilesById?.[selectedAuthProfileId]?.name ?? null)
          : null;

      const primaryTemplate =
        selectedTemplates.find((template) => template.isPrimary) ??
        defaultTemplates.find((template) => template.isPrimary);

      const formatTemplateLabel = (template: TemplateWithPrimary): string => {
        const localeLabel = isMultiLocale ? getLocaleLabel(template.locale) : null;
        return isNotNil(localeLabel) ? `${template.title} ${localeLabel}` : template.title;
      };

      const hasSelectedTemplates = isNotNilOrEmpty(selectedTemplates);

      const options: TemplateReturnType['options'] = match({
        hasSelectedTemplates,
        isMultiLocale,
      })
        .with({hasSelectedTemplates: true}, () => [
          {
            label: selectedAuthProfileLabel ?? i18n.t('general.labels.template'),
            options: selectedTemplates.map((template) => ({
              label: formatTemplateLabel(template),
              value: template.id,
            })),
          },
          {
            label: i18n.t('entity.authorizationProfiles.labels.withoutAuthorization'),
            options: defaultTemplates.map((template) => ({
              label: formatTemplateLabel(template),
              value: template.id,
            })),
          },
        ])
        .with({hasSelectedTemplates: false, isMultiLocale: true}, () =>
          groupTemplatesByLocale(defaultTemplates).map((group) => ({
            label: group.label,
            options: group.templates.map((template) => ({
              label: template.title,
              value: template.id,
            })),
          }))
        )
        .otherwise(() => [
          {
            label: selectedAuthProfileLabel ?? i18n.t('general.labels.template'),
            options: [],
          },
          {
            label: undefined,
            options: defaultTemplates.map((template) => ({
              label: template.title,
              value: template.id,
            })),
          },
        ]);

      const getDropdownComponent: TemplateReturnType['getDropdownComponent'] = (props) => {
        const localeGroups = isMultiLocale
          ? groupTemplatesByLocale(defaultTemplates, props.customerPreferredLocale)
          : null;

        return (
          <Dropdown
            data-testid={props['data-testid']}
            isHeightLimited
            isDisabled={props.isDisabled}
            dropdownControl={
              <Button
                variant={props.variant ?? 'secondary'}
                rightIcon="navigation/expand_more"
                title={props.title || i18n.t('general.labels.template')}
                isDisabled={props.isDisabled}
              />
            }
          >
            <Show when={isNotNilOrEmpty(selectedTemplates) && isAuthProfilesTemplatesEnabled}>
              <Label>{selectedAuthProfileLabel ?? i18n.t('general.labels.template')}</Label>
              <Separator spacing={0} />
              {selectedTemplates?.map((template, index) => (
                <DropdownItem
                  data-testid={suffixTestId(`auth-profile-template[${index}]`, props)}
                  key={template.id}
                  label={template.title}
                  onClick={() => props.onChange(template.id)}
                />
              ))}

              <Space vertical={2} />
              <Label>{i18n.t('entity.authorizationProfiles.labels.withoutAuthorization')}</Label>
              <Separator spacing={0} />
            </Show>

            {localeGroups ? (
              localeGroups.map((group, groupIndex) => (
                <Fragment key={group.key}>
                  <Show when={groupIndex > 0}>
                    <Space vertical={2} />
                  </Show>
                  <Label>{group.label}</Label>
                  <Separator spacing={0} />
                  {group.templates.map((template, index) => (
                    <DropdownItem
                      data-testid={suffixTestId(`template-${group.key}[${index}]`, props)}
                      key={template.id}
                      label={template.title}
                      onClick={() => props.onChange(template.id)}
                    />
                  ))}
                </Fragment>
              ))
            ) : (
              <Show when={isNotNilOrEmpty(defaultTemplates)}>
                {defaultTemplates?.map((template, index) => (
                  <DropdownItem
                    data-testid={suffixTestId(`template[${index}]`, props)}
                    key={template.id}
                    label={template.title}
                    onClick={() => props.onChange(template.id)}
                  />
                ))}
              </Show>
            )}
          </Dropdown>
        );
      };

      return {
        defaultTemplates,
        selectedTemplates,
        primaryTemplate,
        selectedAuthProfileLabel,
        options,
        definition,
        getDropdownComponent,
      };
    },
    [
      templates,
      selectedAuthProfileId,
      authorizationProfilesById,
      isAuthProfilesTemplatesEnabled,
      isMultiLocale,
    ]
  );

  return [getTemplatesByCode, {isLoading, isError}] as const;
};

const addIsPrimaryAttribute =
  (definition: DefinitionWithTemplatesResponseBody) => (template: TemplateResponseBody) => {
    const primaryTemplateId = definition.primaryTemplateId;
    const selectedPrimaryTemplateId = definition.authorizationProfiles.find(
      (item) => item.authorizationProfileId === template.authorizationProfileId
    )?.primaryTemplateId;

    return {
      ...template,
      isPrimary: template.id === primaryTemplateId || template.id === selectedPrimaryTemplateId,
    };
  };

const sortByIsPrimary = (a: TemplateWithPrimary, b: TemplateWithPrimary) => {
  const aPrimary = a.isPrimary ? 1 : 0;
  const bPrimary = b.isPrimary ? 1 : 0;

  return bPrimary - aPrimary;
};
